/**
 * GLINT-SHEEN — reflet vivant global sur les vignettes produit.
 *
 * Une bande de lumiere blanche glisse sur les oeuvres. Les visuels etant des
 * WebP transparents poses sur des conteneurs a fond blanc, la bande
 * n'eclaire que les pixels de l'oeuvre : l'effet miroir sans masque, sans
 * blend-mode, sans WebGL. Transform uniquement -> composite, zero layout.
 *
 * Cibles : les diapositives image des cartes produit natives Horizon
 * (`slideshow-slide.product-media-container--image`). Le suffixe --image
 * exclut d'office les diapositives 3D et video. La section GLINT Collection
 * Offer possede sa propre implementation (avec balayage d'entree) dans
 * glint-collection-offer.liquid — les deux ne se recouvrent jamais.
 *
 * Bureau (souris fine) : la lumiere suit le curseur avec un leger retard,
 * ou qu'il soit sur la page — un seul point de lumiere, coherent d'une
 * carte a l'autre, comme une fenetre reflechie dans une rangee de miroirs.
 * Mobile : la lumiere traverse chaque vignette au fil du defilement.
 * prefers-reduced-motion : rien.
 *
 * Les cartes ajoutees apres coup (filtres, pagination, quick add) sont
 * prises en charge par un MutationObserver.
 */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // La classe, sans presumer de la balise : en mobile les medias produit
  // sont des <slideshow-slide>, en bureau des <li> (galerie en grille).
  // Le suffixe --image exclut d'office les diapositives 3D et video.
  // [data-glint-sheen-target] : cibles declarees par les sections GLINT
  // (ex. la scene du Mirror Moment) sans dependre des classes du theme.
  // .glint-product-card__media : les vignettes du Bundle Builder
  // (demande client 24/08 — reflet + gyroscope sur la page bundle aussi).
  var SELECTOR = '.product-media-container--image, [data-glint-sheen-target], .glint-product-card__media';
  var REST = -0.35;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var entries = [];
  var rafId = null;
  var pointerX = null;
  var visibleCount = 0;

  // ---- Styles, injectes une seule fois -----------------------------------
  function injectStyles() {
    var style = document.createElement('style');
    style.setAttribute('data-glint-sheen', 'styles');
    style.textContent =
      '.glint-sheen{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:2}' +
      '.glint-sheen::before{content:"";position:absolute;top:-25%;bottom:-25%;left:0;width:55%;' +
      'background:linear-gradient(100deg,rgba(255,255,255,0) 0%,rgba(255,255,255,0.30) 38%,' +
      'rgba(255,255,255,0.55) 50%,rgba(255,255,255,0.30) 62%,rgba(255,255,255,0) 100%);' +
      'transform:translateX(calc(var(--glint-gx, -0.35) * 340% - 130%)) skewX(-14deg)}';
    document.head.appendChild(style);
  }

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  // Gyroscope (mobile) : la lumiere suit l'inclinaison du telephone.
  // iOS exige une permission liee a un geste : demandee au premier
  // toucher. Refus ou API absente : le defilement garde la main.
  var gyroTilt = null;
  var lastScrollAt = 0;

  function enableGyro() {
    window.addEventListener('deviceorientation', function (event) {
      if (event.gamma === null || event.gamma === undefined) return;
      gyroTilt = clamp(event.gamma / 30, -1, 1);
      wake();
    });
  }


  // ---- Moteur ------------------------------------------------------------
  function computeTarget(entry) {
    var rect = entry.el.getBoundingClientRect();
    if (finePointer) {
      if (pointerX === null) return REST;
      var x = (pointerX - rect.left) / Math.max(rect.width, 1);
      return 0.5 + (clamp(x, -1.2, 2.2) - 0.5) * 0.85;
    }
    var vh = window.innerHeight || 1;
    // Hybride (decide 24/08) : pendant le defilement, le balayage au
    // scroll garde la main (il rend tres bien) ; apres ~1 s pose sur un
    // ecran, le gyroscope prend le dessus. Le lissage fait la transition.
    if (gyroTilt !== null && Date.now() - lastScrollAt > 1000) {
      // Le reflet physique : l'angle du telephone place la lumiere,
      // avec une legere parallaxe selon la position de la vignette.
      var parallax = ((rect.top + rect.height / 2) / vh - 0.5) * 0.2;
      return 0.5 + gyroTilt * 0.55 + parallax;
    }
    var p = 1 - (rect.top + rect.height / 2) / (vh + rect.height);
    return clamp(p, 0, 1) * 1.7 - 0.35;
  }

  // ⚠️ DEUX PHASES, ET L'ORDRE COMPTE (corrige le 31/08).
  //
  // Avant, on lisait la position d'une vignette (getBoundingClientRect) puis
  // on ecrivait aussitot son style, vignette par vignette. Or chaque ecriture
  // invalide la mise en page : la lecture suivante forcait donc le navigateur
  // a TOUT recalculer. Autant de recalculs qu'il y a de vignettes visibles,
  // soixante fois par seconde — et il y en a 48 sur la page collection.
  //
  // Le navigateur le disait lui-meme en console :
  //   « Rendering was performed in a subtree hidden by content-visibility »
  // Les vignettes vivent dans des diapositives en `content-visibility: auto` ;
  // en les mesurant on forcait le rendu de ce que le navigateur voulait
  // justement s'epargner. On payait deux fois.
  //
  // On LIT tout d'abord, on ECRIT tout ensuite : une seule mise en page par
  // image au lieu d'une par vignette. Le rendu a l'ecran est identique.
  function tick() {
    rafId = null;

    // Phase 1 — lectures seules. Aucune ecriture ici, sinon on retombe dans
    // le defaut qu'on corrige.
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].visible) entries[i].cible = computeTarget(entries[i]);
    }

    // Phase 2 — ecritures seules.
    // 11/09 : la boucle ne se rearme que tant qu'une vignette visible n'a pas
    // rejoint sa cible. Avant, elle tournait a 60 images/s pour toujours des
    // qu'une vignette etait a l'ecran (mesure : 721 lectures de geometrie en
    // 3 s au repos) — sur telephone, le fil principal occupe en permanence.
    // Souris, defilement, gyroscope et visibilite la reveillent (wake).
    var actif = false;
    for (var j = 0; j < entries.length; j++) {
      var entry = entries[j];
      if (!entry.visible) continue;
      var delta = entry.cible - entry.current;
      if (Math.abs(delta) > 0.0015) {
        entry.current += delta * 0.09;
        entry.el.style.setProperty('--glint-gx', entry.current.toFixed(4));
        actif = true;
      }
    }

    if (actif && visibleCount > 0) rafId = window.requestAnimationFrame(tick);
  }

  function wake() {
    if (rafId === null && visibleCount > 0) rafId = window.requestAnimationFrame(tick);
  }

  // ---- Attachement -------------------------------------------------------
  var io = null;
  if (typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver(function (ioEntries) {
      ioEntries.forEach(function (ioEntry) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].el !== ioEntry.target) continue;
          if (entries[i].visible !== ioEntry.isIntersecting) {
            entries[i].visible = ioEntry.isIntersecting;
            visibleCount += ioEntry.isIntersecting ? 1 : -1;
          }
          break;
        }
      });
      wake();
    }, { threshold: 0.1 });
  }

  // ⚠️ DEUX PHASES ICI AUSSI (corrige le 11/09). Le Wall Studio re-rend ses
  // 69 cartes a chaque piece posee ; l'observateur appelait attach() carte
  // par carte, et chaque attach LISAIT getComputedStyle puis ECRIVAIT
  // (attribut + bande) : 69 recalculs de style complets d'affilee, ~550 ms
  // de gel a chaque clic (mesure). On collecte les cibles, on lit toutes les
  // positions, puis on ecrit — une seule mise en page. Rendu identique.
  function attach(el) {
    if (el.hasAttribute('data-glint-sheen')) return;

    el.setAttribute('data-glint-sheen', 'true');

    var band = document.createElement('span');
    band.className = 'glint-sheen';
    band.setAttribute('aria-hidden', 'true');
    el.appendChild(band);

    // `cible` est remplie par la phase de lecture de tick() ; on l'initialise
    // au repos pour qu'une ecriture ne puisse jamais lire un undefined.
    entries.push({ el: el, current: REST, cible: REST, visible: false });
    if (io) io.observe(el);
  }

  // Phase 0 — collecte des cibles d'un sous-arbre (aucune lecture de style).
  function collect(rootNode, into) {
    if (rootNode.nodeType !== 1) return;
    if (rootNode.matches && rootNode.matches(SELECTOR)) into.push(rootNode);
    var found = rootNode.querySelectorAll ? rootNode.querySelectorAll(SELECTOR) : [];
    for (var i = 0; i < found.length; i++) into.push(found[i]);
  }

  // Phase 1 — lectures seules : la bande est en position:absolute, sans
  // ancetre positionne elle s'ancrerait n'importe ou (elements du zoom, en
  // static). Phase 2 — ecritures seules.
  function attachAll(cibles) {
    var ok = [];
    for (var i = 0; i < cibles.length; i++) {
      var el = cibles[i];
      if (el.hasAttribute('data-glint-sheen')) continue;
      if (getComputedStyle(el).position === 'static') continue;
      ok.push(el);
    }
    for (var j = 0; j < ok.length; j++) attach(ok[j]);
  }

  function scan(rootNode) {
    var cibles = [];
    collect(rootNode, cibles);
    attachAll(cibles);
  }

  function init() {
    injectStyles();
    scan(document.body);

    // Les cartes arrivees apres coup (filtres, pagination, quick add).
    new MutationObserver(function (mutations) {
      // Toutes les cibles de la salve d'abord, puis une seule passe
      // lectures / ecritures (69 cartes = une mise en page, pas 69).
      var cibles = [];
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) collect(added[j], cibles);
      }
      if (cibles.length) attachAll(cibles);
    }).observe(document.body, { childList: true, subtree: true });

    if (finePointer) {
      document.addEventListener('mousemove', function (e) {
        pointerX = e.clientX;
        wake();
      });
      document.documentElement.addEventListener('mouseleave', function () {
        pointerX = null;
        wake();
      });
    } else {
      // Le defilement garde la main pendant le geste (mode hybride).
      window.addEventListener('scroll', function () {
        lastScrollAt = Date.now();
        wake();
      }, { passive: true });

      // Gyroscope UNIQUEMENT la ou il est gratuit (Android) : sur iOS il
      // exigerait une popup de permission au premier toucher — un vrai
      // risque de conversion (decide avec le client le 24/08 : zero
      // friction). Sur iPhone, le balayage au defilement reste.
      if (window.DeviceOrientationEvent &&
          typeof window.DeviceOrientationEvent.requestPermission !== 'function') {
        enableGyro();
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
